import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { getSimulatedCodes } from 'quote/helpers/simulated-rejection-codes';
import { errorClassificationLookup, errorCodes, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';
import omit from 'lodash-es/omit';

import {
  mapRequestQuoteToInputType,
  hasRejection,
  formattedError,
  mapRevisedBranchPlusQuoteDetails
} from 'core/helpers/quoter.service';
import { track } from 'core/helpers/analytics';
import { withRetry } from 'core/helpers/with-retry';
import { isVinLookupUnavailable } from 'common/helpers/vin-lookup-error';
import { appConfig, brandConstants } from 'brand-config';

// When false (Forreston/Oakwood/CFM), the named-insured-level licensed-state gate is
// skipped — the licensed-state check is enforced per farm location instead. (Issue #30876)
const enforceLicensedStateOnNI = () => appConfig?.featureFlags?.enforceLicensedStateOnNamedInsured !== false;
import {
  REQUEST_QUOTE,
  REQUEST_BRANCH_PLUS_QUOTE,
  SEND_CLUSTER_LINK,
  ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
  ON_BRANCH_PLUS_OFFER_GENERATED
} from './quote.queries';
import { GET_OFFER } from '../offer-store/offer-queries';

const ErrorType = types.model({
  type: types.maybe(types.string),
  code: types.union(types.string, types.number),
  message: types.maybe(types.string),
  data: types.maybe(types.frozen()),
  level: types.maybe(types.string),
  handleAs: types.maybe(types.string),
  simulated: types.optional(types.boolean, false)
});

const SimulationConfig = types.model('SimulationConfig', {
  source: types.maybeNull(types.string),
  noHitDataPulls: types.optional(types.array(types.string), [])
});

let subscription = null;

export const QuoteStore = types
  .model({
    loading: types.boolean,
    errors: types.array(ErrorType),
    prefillData: types.maybeNull(types.frozen()),
    // Offer a requote was prefilled from ({ offerId, webUserId }), so the new lineage's analytics can
    // say whether the prospect kept their webUserId (prefillData is only the quote, without ids).
    prefillSource: types.maybeNull(types.frozen()),
    overriddenProtectedFields: types.array(types.string),
    offer: types.maybeNull(types.frozen()),
    simulationConfig: types.optional(SimulationConfig, {})
  })
  .volatile(() => ({
    vinFailureTracker: { vin: '', count: 0 }
  }))
  .actions((self) => ({
    generateBranchPlusOffersFromOfferAction: flow(function* generateBranchPlusOffersFromOffer({
      quoteDetails,
      webUserId,
      rep,
      policyId,
      offerId,
      policyType
    }) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      try {
        const { client } = getEnv(self);
        self.loading = true;

        const parent = getParent(self);
        const customerId = policyId ? policyId.slice(0, 9) : null;
        yield parent.account.sendEventAsMember({
          eventName: 'Staff Generated Branch Plus Quote - Customer',
          properties: JSON.stringify({ webUserId, customerId, staffRep: rep.username, policyType }),
          userId: customerId || webUserId
        });

        track('Staff Generated Branch Plus Quote', { webUserId, customerId, staffRep: rep.username, policyType });

        const response = yield client.query({
          query: ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
          variables: {
            quoteDetails: mapRevisedBranchPlusQuoteDetails(quoteDetails),
            policyType,
            policyId,
            offerId,
            webUserId
          }
        });

        const branchPlusResponse = yield new Promise((resolve, reject) => {
          const observable = client.subscribe({
            query: ON_BRANCH_PLUS_OFFER_GENERATED,
            variables: { taskId: response.data.asyncGenerateBranchPlusOffersFromOffer.taskId }
          });

          subscription = observable.subscribe({
            next: ({ data }) => {
              try {
                const { onBranchPlusOfferGenerated } = data;
                if (onBranchPlusOfferGenerated.error) {
                  reject(onBranchPlusOfferGenerated.error);
                } else if (onBranchPlusOfferGenerated.data) {
                  resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                }
              } catch (err) {
                reject(err);
              } finally {
                subscription.unsubscribe();
              }
            },
            error: (err) => {
              reject(err);
            }
          });
        }).catch((err) => {
          subscription.unsubscribe();
          throw err;
        });

        if (branchPlusResponse?.branchPlusOffers?.length > 0 && branchPlusResponse.offerId) {
          let lowestPrice = Number.POSITIVE_INFINITY;
          let lowestPricedCarrier = '';
          const successfulOffers = [];

          branchPlusResponse.branchPlusOffers.forEach((offer) => {
            if (offer?.status === 'success') {
              successfulOffers.push(offer);

              if (offer?.policyType === policyType && offer?.premium && offer.premium < lowestPrice) {
                lowestPrice = offer.premium;
                lowestPricedCarrier = offer.carrierName;
              }
            } else {
              track('Staff Generated Branch Plus Quote Response Error', {
                webUserId,
                staffRep: rep.username,
                carrier: offer?.carrierName,
                customerId,
                policyType
              });
            }
          });

          successfulOffers.forEach((offer) => {
            track('Staff Generated Branch Plus Quote Response Success', {
              webUserId,
              premium: offer.premium,
              staffRep: rep.username,
              carrier: offer.carrierName,
              customerId,
              policyType,
              lowestPricedCarrier,
              lowestPrice: lowestPrice === Number.POSITIVE_INFINITY ? null : lowestPrice
            });
          });

          return branchPlusResponse;
        }
      } catch (error) {
        track('Staff Generated Branch Plus Quote Response Error', {
          webUserId,
          staffRep: rep.username,
          policyType
        });
        console.error('Error in requestBranchPlusQuote:', error);
        throw error;
      } finally {
        self.loading = false;
      }
    }),
    requestQuoteAction: flow(function* requestQuote(userInput, allowedStates, fromOfferPage = false) {
      let requestFailureCategory;
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (!enforceLicensedStateOnNI() || allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Quote', {
            state: userInput.state,
            policyType: userInput.policyType,
            affinity: userInput.affinity,
            leadSource: userInput.leadSource,
            staffRep: userInput.rep
          });

          // One-shot semantics: if the user has filled the prior-coverage resolution fields
          // (policyLimitBIPD / priorCoverageInYears), clear the CV simulation so this retry
          // goes through real CV instead of re-simulating.
          if (
            self.simulationConfig.noHitDataPulls.includes('coverageVerifier') &&
            (userInput.policyLimitBIPD || userInput.priorCoverageInYears != null)
          ) {
            self.toggleNoHitDataPull('coverageVerifier');
          }

          const hasSimulation = self.simulationConfig.source || self.simulationConfig.noHitDataPulls.length > 0;
          const simulatedCodes = getSimulatedCodes(self.simulationConfig.noHitDataPulls);

          // data is null (not an object) on an exhausted timeout response, so don't nest-destructure
          const { data, errors } = yield withRetry(
            () =>
              client.query({
                query: REQUEST_QUOTE,
                variables: {
                  ...mapRequestQuoteToInputType(userInput),
                  // The offer-page resubmit isn't a prefilled requote, so a leftover prefillSource must not apply
                  sourceOfferId: fromOfferPage ? undefined : self.prefillSource?.offerId,
                  ...(hasSimulation && {
                    simulationConfig: {
                      source: self.simulationConfig.source || undefined,
                      noHitDataPulls: self.simulationConfig.noHitDataPulls.length
                        ? self.simulationConfig.noHitDataPulls
                        : undefined
                    }
                  })
                },
                fetchPolicy: 'no-cache'
              }),
            'requestQuote',
            // ~30s per timed-out attempt, so cap at 3 to bound the worst-case wait
            3
          );
          const offer = data?.offer;
          if (offer) {
            self.offer = offer;
          }
          if (errors?.length) {
            self.errors = errors.map((error) => {
              try {
                const parsed = JSON.parse(error.message);
                const code = Number(parsed.code);
                return {
                  code,
                  message: parsed.message,
                  // structured [code][metadata] payload (e.g. the 5010 lookupUnavailable flag)
                  data: parsed.metadata,
                  simulated: simulatedCodes.has(code)
                };
              } catch {
                // non-JSON message (e.g. an exhausted ExecutionTimeout) — surface it as-is
                return { code: error.errorType || 'UNKNOWN', message: error.message };
              }
            });
            self.trackVinNoHit(userInput);
            self.loading = false;
            return fromOfferPage || offer ? undefined : self.getRaterErrorCategory();
          }

          self.offer = offer;
          self.resetVinFailureTracker();
          self.loading = false;

          if (hasRejection(offer)) {
            self.errors = offer.rejections.map(({ type, value, data, level }) => {
              const code = value;
              return {
                type,
                code,
                data: data && JSON.parse(data),
                level,
                simulated: simulatedCodes.has(Number(code))
              };
            });
          }

          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error(`Error in requestQuote: ${error}`);
          self.loading = false;
          self.handleError(error);
          // GraphQL errors reject under Apollo's default errorPolicy and land here (not in the
          // resolved `errors` branch above), so the VIN no-hit counter must also run on this path
          self.trackVinNoHit(userInput);
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
          requestFailureCategory = error?.graphQLErrors?.length ? self.getRaterErrorCategory() : 'request-failed';
        }
      } else {
        const notLicensedError = {
          message: `You are not licensed in this state, if you believe this is a mistake, please contact ${brandConstants.SHORT_NAME} at ${brandConstants.CUSTOMER_SERVICE_PHONE_LETTERS} (${brandConstants.CUSTOMER_SERVICE_PHONE}) ${brandConstants.SUPPORT_EMAIL}`
        };
        self.loading = false;
        self.handleError(notLicensedError);
        requestFailureCategory = 'not-licensed';
      }
      if (fromOfferPage) {
        return self.offer?.id;
      }
      // Why no offer came back, for the quote page's Staff New Quote Error event; undefined on success
      return requestFailureCategory;
    }),
    requestBranchPlusQuoteAction: flow(function* requestBranchPlusQuoteAction(userInput, allowedStates) {
      // Same contract as requestQuoteAction: a category when the request failed (self.offer may
      // still hold a previous offer), undefined otherwise.
      let requestFailureCategory;
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (!enforceLicensedStateOnNI() || allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Branch Plus Quote', {
            state: userInput.state,
            policyType: userInput.policyType,
            affinity: userInput.affinity,
            leadSource: userInput.leadSource,
            staffRep: userInput.rep
          });

          const response = yield withRetry(
            () =>
              client.query({
                query: REQUEST_BRANCH_PLUS_QUOTE,
                variables: mapRequestQuoteToInputType(userInput),
                fetchPolicy: 'no-cache'
              }),
            'asyncGenerateBranchPlusOffers'
          );

          const { offerId } = yield new Promise((resolve, reject) => {
            const observable = client.subscribe({
              query: ON_BRANCH_PLUS_OFFER_GENERATED,
              variables: { taskId: response.data.BranchPlusOfferResponse.taskId }
            });

            const timeoutId = setTimeout(
              () => {
                subscription?.unsubscribe();
                reject(new Error('Branch Plus Timeout'));
              },
              5 * 60 * 1000
            );

            subscription = observable.subscribe({
              next: ({ data }) => {
                try {
                  const { onBranchPlusOfferGenerated } = data;
                  if (onBranchPlusOfferGenerated.error) {
                    clearTimeout(timeoutId);
                    reject(onBranchPlusOfferGenerated.error);
                  } else if (onBranchPlusOfferGenerated.data) {
                    clearTimeout(timeoutId);
                    resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                  }
                } catch (err) {
                  clearTimeout(timeoutId);
                  reject(err);
                }
              },
              error: (err) => {
                clearTimeout(timeoutId);
                reject(err);
              }
            });
          }).catch((err) => {
            subscription.unsubscribe();
            throw err;
          });

          const offerResponse = yield client.query({
            query: GET_OFFER,
            variables: { offerId },
            fetchPolicy: 'no-cache'
          });

          if (offerResponse.data && offerResponse.data.offer) {
            const { offer } = offerResponse.data;
            self.offer = offer;

            if (hasRejection(offer)) {
              self.errors = offer.rejections.map(({ type, value, data, level }) => {
                return {
                  type,
                  code: value,
                  data: data && JSON.parse(data),
                  level
                };
              });
            }
          } else {
            requestFailureCategory = 'no-offer';
          }

          self.loading = false;
          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: self.offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error('Error in requestBranchPlusQuote:', error);
          self.loading = false;
          self.handleError(error);
          requestFailureCategory = error?.graphQLErrors?.length ? self.getRaterErrorCategory() : 'request-failed';
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        }
      } else {
        const notLicensedError = {
          message: `You are not licensed in this state, if you believe this is a mistake, please contact ${brandConstants.SHORT_NAME} at ${brandConstants.CUSTOMER_SERVICE_PHONE_LETTERS} (${brandConstants.CUSTOMER_SERVICE_PHONE}) ${brandConstants.SUPPORT_EMAIL}`
        };
        requestFailureCategory = 'not-licensed';
        self.loading = false;
        self.handleError(notLicensedError);
      }
      return requestFailureCategory;
    }),
    sendClusterLink: flow(function* sendClusterLink(offerId, email, quote, agent) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SEND_CLUSTER_LINK,
        variables: {
          offerId,
          revisedQuoteDetails: quote,
          email,
          agent
        }
      });
    }),
    fetchAffinityData: flow(function* fetchAffinityData(affinity) {
      if (affinity) {
        const { affinityLookups } = getParent(self);
        yield affinityLookups.getByAffinity(affinity);
      }
    }),
    handleError: (error) => {
      const parsed = formattedError(error);
      if (parsed) {
        // ErrorType carries structured payloads on `data`; formattedError surfaces the backend's
        // [code][metadata] bracket as `metadata`, so remap it (ErrorType has no metadata field)
        const { metadata, ...rest } = parsed;
        self.errors = [...self.errors, { ...rest, ...(metadata != null && { data: metadata }) }];
      }
    },
    setPrefillData(offer, skipRejectionCheck = false, preselectBundleOption = false) {
      self.overriddenProtectedFields = [];
      if (!skipRejectionCheck) {
        if (hasRejection(offer)) {
          self.errors = offer.rejections.map((rejection) => ({
            type: rejection.type,
            code: rejection.value,
            data: rejection.data && JSON.parse(rejection.data),
            level: rejection.level
          }));
        }
      }
      const prefillData = omit(offer.quote, '__typename');
      if (preselectBundleOption) {
        prefillData.selectedOption = 'HA';
      }
      self.prefillData = prefillData;
      self.prefillSource = offer?.id || offer?.webUserId ? { offerId: offer.id, webUserId: offer.webUserId } : null;
    },
    clearPrefillData() {
      self.prefillData = null;
      self.prefillSource = null;
      self.overriddenProtectedFields = [];
    },
    clearError(code) {
      self.errors = self.errors.filter((err) => err.code !== code);
    },
    trackVinNoHit(userInput = {}) {
      const submittedVin = userInput.VIN;
      const vinLookupErrors = self.errors.filter((error) => Number(error.code) === errorCodes.INVALIDVIN);
      // outage-flagged 5010s don't count: retrying THOSE is correct, so they must not push the
      // copy into "resubmitting won't change the result" territory
      const hasVinNoHit = vinLookupErrors.some((error) => !isVinLookupUnavailable(error));
      if (hasVinNoHit && submittedVin) {
        self.vinFailureTracker = {
          vin: submittedVin,
          count: self.vinFailureTracker.vin === submittedVin ? self.vinFailureTracker.count + 1 : 1
        };
      } else {
        self.vinFailureTracker = { vin: '', count: 0 };
      }
      if (vinLookupErrors.length && submittedVin) {
        // per-cause 5010 telemetry: confirms in production that the outage flag survives the
        // pipeline (cross-check against backend "vehicle lookup unavailable" logs) and feeds
        // the retry-friction re-measure for #33196
        const lookupUnavailable = vinLookupErrors.some((error) => isVinLookupUnavailable(error));
        const offerId = self.offer?.id ?? null;
        track(lookupUnavailable ? 'Staff Quote VIN Lookup Unavailable' : 'Staff Quote VIN Not Found', {
          vin: submittedVin,
          lookupUnavailable,
          vinRetryCount: self.vinFailureTracker.count,
          policyType: userInput.policyType,
          state: userInput.state,
          rep: userInput.rep?.username ?? userInput.rep,
          offerId,
          offerLink: offerId && globalThis.window ? `${globalThis.window.location.origin}/offer/${offerId}` : null,
          errorMessage: vinLookupErrors[0]?.message
        });
      }
    },
    resetVinFailureTracker() {
      self.vinFailureTracker = { vin: '', count: 0 };
    },
    setErrorToShowAsRejection(code) {
      self.errors = self.errors.map((err) => {
        if (err.code === code) {
          return { ...err, handleAs: 'REJECTION' };
        }
        return err;
      });
    },
    getAlerts(codesArray = [], policyType) {
      if (!policyType) {
        return self.errors.filter((error) => {
          const isAlert =
            error.code &&
            ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(errorClassificationLookup[Number(error.code)]);
          const handleAsAlert = error.handleAs === 'ALERT' || error.handleAs === 'BYPASS_ALERT';
          const includeAlert = codesArray.length === 0 || codesArray.includes(Number(error.code));
          return (isAlert || handleAsAlert) && includeAlert && error.handleAs !== 'REJECTION';
        });
      }
      return self.errors.filter((error) => {
        // if this is a bypass alert or dismissible rejection and the requested policy type is not in the rejection array, dont show this error
        if (error.code) {
          const classification = errorClassificationLookup[Number(error.code)];
          if (classification === 'BYPASS_ALERT' || classification === 'DISMISSIBLE_REJECTION') {
            if (!rejectionTypeToPolicyTypeMap[error.type]?.includes(policyType)) {
              return false;
            }
          }
        }
        const isAlert =
          error.code &&
          ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(errorClassificationLookup[Number(error.code)]);
        const handleAsAlert = error.handleAs === 'ALERT' || error.handleAs === 'BYPASS_ALERT';
        const includeAlert = codesArray.length === 0 || codesArray.includes(Number(error.code));
        return (isAlert || handleAsAlert) && includeAlert && error.handleAs !== 'REJECTION';
      });
    },
    getBlocks() {
      return self.errors.filter((error) => {
        const isBlock = !error.code || errorClassificationLookup[Number(error.code)] === 'BLOCK';
        const handleAsBlock = error.handleAs === 'BLOCK';

        return isBlock || handleAsBlock;
      });
    },
    // Rater alerts, blocks and rejections come back as GraphQL errors, which reject under Apollo's
    // default errorPolicy; name the kind so they aren't reported as request failures
    getRaterErrorCategory() {
      if (self.getAlerts().length) {
        return 'alert';
      }
      // getBlocks also counts code-less errors (schema validation, unparseable messages) as blocks
      if (self.getBlocks().some((error) => error.code)) {
        return 'block';
      }
      if (self.rejections.length) {
        return 'rejection';
      }
      return 'graphql-error';
    },
    getOfferDetails() {
      return {
        offer: self.offer,
        alerts: self.getAlerts(),
        blocks: self.getBlocks(),
        rejections: self.rejections,
        errorCodes: self.errors.map((error) => error.code).filter((code) => code != null)
      };
    },
    cleanup() {
      if (subscription) {
        subscription.unsubscribe();
      }
      self.loading = false;
    },
    setSimulationSource(source) {
      self.simulationConfig.source = source || null;
    },
    toggleNoHitDataPull(source) {
      if (self.simulationConfig.noHitDataPulls.includes(source)) {
        self.simulationConfig.noHitDataPulls = self.simulationConfig.noHitDataPulls.filter((dp) => dp !== source);
      } else {
        self.simulationConfig.noHitDataPulls.push(source);
      }
    }
  }))
  .views((self) => ({
    get rejections() {
      return self.errors.filter((err) => {
        // if error code is 10000 or greater, it's a rejection
        if (
          err.code &&
          err.code >= 10000 &&
          ((!err.handleAs && !errorClassificationLookup[err.code]) || err?.handleAs === 'REJECTION')
        ) {
          return true;
        }
        return false;
      });
    }
  }));
